import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getApiBaseUrl } from '../../utils/apiConfig';

// Types
interface Account {
  id: number;
  accountNumber: string;
  accountType: string;
  balance: number;
  currency: string;
  status: string;
  createdAt: string;
}

interface AccountState {
  accounts: Account[];
  isLoading: boolean;
  error: string | null;
}

// Initial state
const initialState: AccountState = {
  accounts: [],
  isLoading: false,
  error: null,
};

// Async thunk
export const fetchAccounts = createAsyncThunk(
  'accounts/fetchData',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { auth: { token: string | null; type: 'JWT' | 'SESSION' | null } };
      const token = state.auth.token;
      const authType = state.auth.type;
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      // Only add Authorization header for JWT auth
      if (token && authType === 'JWT') {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${getApiBaseUrl()}/accounts`, {
        method: 'GET',
        headers,
        credentials: authType === 'SESSION' ? 'include' : 'omit',
      });

      const json = await response.json();
      if (!response.ok) {
        if (json.errors) return rejectWithValue(json.errors.join(', '));
        return rejectWithValue(json.message || 'Failed to fetch accounts');
      }
      return json.data;
    } catch (error) {
      return rejectWithValue('Network error occurred');
    }
  }
);

// Slice
const accountSlice = createSlice({
  name: 'accounts',
  initialState,
  reducers: {
    clearAccountError: (state) => {
      state.error = null;
    },
    addAccount: (state, action: PayloadAction<Account>) => {
      state.accounts.push(action.payload);
    },
    updateAccount: (state, action: PayloadAction<Account>) => {
      const index = state.accounts.findIndex(a => a.id === action.payload.id);
      if (index !== -1) {
        state.accounts[index] = action.payload;
      }
    },
    updateAccountBalance: (state, action: PayloadAction<{ accountId: number; newBalance: number }>) => {
      const account = state.accounts.find(a => a.id === action.payload.accountId);
      if (account) {
        account.balance = action.payload.newBalance;
      }
    },
    clearAccounts: (state) => {
      state.accounts = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAccounts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAccounts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.accounts = action.payload;
        state.error = null;
      })
      .addCase(fetchAccounts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { 
  clearAccountError, 
  addAccount, 
  updateAccount, 
  updateAccountBalance, 
  clearAccounts 
} = accountSlice.actions;
export default accountSlice.reducer; 